import React, { useState } from 'react';
import { columns } from './OverviewTableModels';
import { useTravelRequestsQuery } from 'api/services/TravelRequests';
import { Spinner } from 'components/Spinner';
import { PageHeader } from 'routes/layouts/pageHeader';
import {
  AlertDescription,
  Box,
  Card,
  CardBody,
  Container,
  Image,
  Stack,
  Link,
  Alert as ChAlert,
  Text as TextComponent,
  AlertIcon,
  FormLabel,
} from '@chakra-ui/react';
import { Heading } from '@chakra-ui/layout';
import { Button, Select, Table } from '@landsvirkjun/react.common';
import { useNavigate, useLocation } from 'react-router-dom';
import { Alert } from 'components/Alert/Alert';
import { NoData } from 'components/NoData/NoData';
import { TravelRequestResult } from 'api/types/types';
import { navigateFromRow } from './navigateFromRow';
import { useAuth } from 'components/Auth/AuthProvider';
import { STATUS_OPTIONS } from 'utils/statusUtils';

type OverviewTableProps = {
  role?: 'approver' | undefined;
};

export const OverviewTable = ({ role }: OverviewTableProps): JSX.Element => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const location = useLocation();
  const adminPath = location?.pathname?.startsWith('/admin/') ? '/admin' : '';
  const approver = location?.pathname?.startsWith('/samthykktir');
  const { data = [], isFetching, isError } = useTravelRequestsQuery(role || '');

  const [selectedStatusValues, setSelectedStatusValues] = useState<string[]>([
    'PENDING',
    'COST_CONFIRMED',
  ]);

  if (isError) {
    return (
      <Alert
        status="error"
        errorText="Eitthvað fór úrskeiðis. Reyndu aftur síðar"
      />
    );
  }

  if (isFetching) {
    return <Spinner delay={300} />;
  }

  const reversedDataOrder = [...data].reverse();

  const filteredData = reversedDataOrder.filter((request) => {
    if (
      selectedStatusValues.length === 0 ||
      selectedStatusValues.includes('ALL')
    ) {
      return true;
    }
    return selectedStatusValues.includes(request.status.shortName);
  });

  const handleRowClick = (row: any): void => {
    const { id, status, erpId } = row.original as TravelRequestResult;
    const { shortName } = status;
    navigateFromRow(id, role, shortName, erpId, navigate);
  };
  return (
    <Container maxW="container.xl" marginStart="ltr" p={0}>
      <PageHeader
        left={
          <Heading as="h2" size="lg">
            {role ? 'Ferðabeiðnir samþykkjanda' : 'Mínar ferðabeiðnir'}
          </Heading>
        }
        right={
          role ? null : (
            <Button
              variant="primary"
              onClick={(): void => navigate('/ferdabeidnir/ferdabeidni')}
            >
              Ný beiðni
            </Button>
          )
        }
      />
      <Box as="div">
        {!adminPath && !approver && (
          <Card
            mt={10}
            mb={20}
            direction={{ base: 'column', sm: 'row' }}
            overflow="hidden"
            variant="outline"
          >
            <Image
              objectFit="contain"
              maxW={{ base: '100%', sm: '200px' }}
              src="/images/earth.svg"
              alt="ferðalög"
            />
            <Stack>
              <CardBody>
                <Heading size="md" mb="2">
                  Hæ, {user?.name}
                </Heading>
                <TextComponent py="2">
                  Ef þú ert að fara að ferðast á vegum Landsvirkjunar erlendis
                  þarftu að velja <strong>&quot;Ný beiðni&quot;</strong> og skrá
                  þar ferðatilhögun. Samþykktarferli og öll tilheyrandi skráning
                  fer fram á þessum vef.
                </TextComponent>
                {/* <Heading size="sm">Ferðakostnaður</Heading> */}
                <TextComponent py="2">
                  Allur ferðakostnaður sem fellur til er skráður á þessum vef,
                  eftir að ferð hefur fengist samþykkt og áður en uppgjöri
                  ferðar lýkur.{' '}
                  <strong>
                    Mikilvægt er nafn Landsvirkjunar komi fram á kvittunum
                  </strong>
                </TextComponent>
                <ChAlert status={'info'} variant={'left-accent'}>
                  <AlertIcon />
                  <AlertDescription maxWidth="sm">
                    <Link
                      href="https://landsvirkjun.workvivo.com/pages/210/reglur-um-ferdakostnad"
                      isExternal
                    >
                      Mikilvægt er að kynna sér reglur um ferðakostnað hér
                    </Link>
                  </AlertDescription>
                </ChAlert>
              </CardBody>

              {/* <CardFooter>
              <Button variant="primary" colorScheme="blue">
                Ný ferðabeiðni
              </Button>
            </CardFooter> */}
            </Stack>
          </Card>
        )}
        {approver ? (
          <Container maxW="container.xl" marginStart="ltr" pl={0}>
            <Box as="div" mt={5}>
              <Box as="div">
                <Box float="right" minW="250px">
                  <FormLabel>Staða beiðnar</FormLabel>
                  <Select
                    isMulti
                    options={STATUS_OPTIONS}
                    onChange={(values: string[]): void => {
                      setSelectedStatusValues(values ?? []);
                    }}
                    value={STATUS_OPTIONS.filter((option) =>
                      selectedStatusValues.includes(option.value)
                    )}
                    placeholder="Staða beiðnar"
                  />
                </Box>
                {filteredData.length > 0 ? (
                  <Table
                    data={filteredData}
                    columns={columns(role || '', adminPath)}
                    withPagination={data.length > 50}
                    enableRowClick
                    onRowClick={handleRowClick}
                  />
                ) : (
                  <TextComponent mt={5}>
                    Engar ferðabeiðnir sem bíða samþykktar
                  </TextComponent>
                )}
              </Box>
            </Box>
          </Container>
        ) : reversedDataOrder.length > 0 ? (
          <Table
            data={reversedDataOrder}
            columns={columns(role || '', adminPath)}
            withPagination={data.length > 50}
            enableRowClick
            onRowClick={handleRowClick}
          />
        ) : (
          <NoData text="Engar ferðabeiðnir skráðar" />
        )}
      </Box>
    </Container>
  );
};
